import React, { useState } from "react";
import { MapPin, Images, Building2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { ImageModal } from "./ImageModal";
import { PageHero } from "./PageHero";
import { mockData, media } from "../mock";

const projectStats = [
  { value: "25+", label: "Projects Completed" },
  { value: "100+", label: "Project Involvement" },
  { value: "0", label: "Safety Incidents" },
  { value: "Multi-State", label: "Coverage" },
];

export const PortfolioPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [initialIndex, setInitialIndex] = useState(0);

  const filters = ["All", "Commercial", "Project Management"];
  const projects =
    filter === "All" ? mockData.projects : mockData.projects.filter((p) => p.type === filter);

  const openModal = (images, idx = 0) => {
    setModalImages(images);
    setInitialIndex(idx);
    setModalOpen(true);
  };

  return (
    <div data-testid="portfolio-page">
      <PageHero
        kicker="Recent Work"
        title="Our Project"
        highlight="Portfolio"
        subtitle="Explore our demolition and project management work across the DMV area and nationwide. Each project reflects our commitment to safety, precision, and excellence."
        bg={media.portfolioImg}
      >
        <div className="mt-10 flex flex-wrap gap-3">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider border transition-colors ${
                filter === f
                  ? "bg-primary text-background border-primary"
                  : "bg-transparent text-white border-white/30 hover:border-primary hover:text-primary"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </PageHero>

      {/* Projects */}
      <section className="tex-plate py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-px">
          {projects.map((project, idx) => (
            <div
              key={project.id}
              data-testid={`portfolio-page-card-${project.id}`}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border ${idx % 2 === 1 ? "lg:[direction:rtl]" : ""}`}
            >
              <div className="relative h-72 lg:h-auto min-h-[320px] overflow-hidden bg-card [direction:ltr] group">
                <img
                  src={project.images[0]}
                  alt={project.title}
                  className="w-full h-full object-cover cursor-pointer transition-transform duration-500 group-hover:scale-105"
                  onClick={() => openModal(project.images, 0)}
                />
                <div className="absolute top-4 left-4 bg-primary text-background text-xs font-bold uppercase tracking-wider px-3 py-1">
                  {project.type}
                </div>
                <button
                  onClick={() => openModal(project.images, 0)}
                  data-testid={`portfolio-page-gallery-btn-${project.id}`}
                  className="absolute bottom-4 right-4 flex items-center gap-2 bg-background/80 border border-border text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 hover:border-primary hover:text-primary transition-colors"
                >
                  <Images className="h-4 w-4" /> View {project.images.length} Photos
                </button>
              </div>
              <div className="bg-card p-8 lg:p-12 flex flex-col justify-center [direction:ltr]">
                <Building2 className="h-7 w-7 text-primary mb-5" />
                <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight text-white leading-tight mb-4">
                  {project.title}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-6">{project.description}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-wider">
                  <MapPin className="h-4 w-4 text-primary" /> {project.location}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ImageModal images={modalImages} isOpen={modalOpen} onClose={() => setModalOpen(false)} initialIndex={initialIndex} />

      {/* Stats */}
      <section className="relative py-16 border-y border-border overflow-hidden">
        <div className="absolute inset-0 tex-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
            {projectStats.map((s) => (
              <div key={s.label} className="py-4 px-4 text-center">
                <div className="font-display text-4xl font-black text-white">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="tex-plate py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">Ready To Add Your Project?</h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">Let&apos;s discuss your demolition or site work needs with a free consultation and detailed estimate.</p>
          <Button data-testid="portfolio-page-quote-btn" size="lg" onClick={() => navigate("/quote")} className="rounded-none font-bold uppercase tracking-widest px-8 py-6">
            Start Your Project <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};
